import { Component, OnInit, OnDestroy } from '@angular/core'
import { AdminChangeLog, AngularRoutes, ChangeLogsEntityEnum, ContractStatus, Invoice, InvoiceStatus, Subscription, SubscriptionType, subscriptionsPath, EnumNames } from '@parkupp/core'
import { collection, collectionData, DocumentData, orderBy, query, Query, QueryDocumentSnapshot, where } from '@angular/fire/firestore'
import { GlobalService } from '../../../services/global.service'
import { ActivatedRoute, Router } from '@angular/router'
import { AdminService } from '../../../services/admin.service'
import { InvoiceService } from '../../../services/invoice.service'
import * as moment from 'moment/moment'
import { AuthenticationService } from 'src/app/services/authentication.service'
import { ClientService } from 'src/app/services/client.service'
import { AdminChangeLogService } from 'src/app/services/admin-change-log.service'
import { EnterpriseContextService } from '../../../services/enterprise-context.service'
import { Subscription as RxSubscription } from 'rxjs'

@Component({
    selector: 'app-admin-list-invoices',
    templateUrl: './admin-list-invoices.component.html',
    styleUrls: ['./admin-list-invoices.component.scss'],
})
export class AdminListInvoicesComponent implements OnInit, OnDestroy {
    private enterpriseSubscription: RxSubscription
    Invoice = Invoice
    readonly AngularRoutes = AngularRoutes
    EnumNames = EnumNames
    SubscriptionType = SubscriptionType
    subscriptions: Subscription[] = []
    invoices: Invoice[] = []
    allInvoices: Invoice[] = []
    InvoiceStatus = InvoiceStatus
    invoiceStates: InvoiceStatus[] = Object.values(InvoiceStatus)

    /** Last document snapshot per page index; page i+1 uses startAfter(cursorsAfterPage[i]). */
    private cursorsAfterPage: QueryDocumentSnapshot<DocumentData>[] = []
    pageIndex = 0
    hasNextPage = false
    itemsPerPage = 100

    // Stats
    invoiceCounts: { [key: string]: number } = {}
    totalDueValue: number = 0

    today: number = moment().toDate().getTime()
    status: InvoiceStatus = InvoiceStatus.DUE
    selectedInvoice: Invoice | null = null

    // Invoice detail modal
    showInvoiceDetailModal = false
    modalInvoice: Invoice | null = null
    modalChangeLogs: AdminChangeLog[] = []
    modalChangeLogsLoading = false
    modalChangeLogsError: string | null = null
    modalInvoiceTab: 'overview' | 'lineItems' = 'overview'

    constructor(
        public globalService: GlobalService,
        private authService: AuthenticationService,
        private route: ActivatedRoute,
        private router: Router,
        private adminService: AdminService,
        private invoiceService: InvoiceService,
        private clientService: ClientService,
        private adminChangeLogService: AdminChangeLogService,
        private enterpriseContext: EnterpriseContextService
    ) {}

    async ngOnInit() {
        this.route.queryParams.subscribe((params) => {
            if (params['status']) {
                this.status = params['status'] as InvoiceStatus
            }
        })
        
        await this.loadData()
        
        // Subscribe to enterprise context changes
        this.enterpriseSubscription = this.enterpriseContext.currentEnterprise$.subscribe(async () => {
            await this.loadData()
        })
    }
    
    ngOnDestroy(): void {
        if (this.enterpriseSubscription) {
            this.enterpriseSubscription.unsubscribe()
        }
    }
    
    async loadData(): Promise<void> {
        // Load invoice counts for stats
        this.loadAllInvoiceCounts()
        
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        const activeContractStatuses: ContractStatus[] = [ContractStatus.paymentInitiated, ContractStatus.active, ContractStatus.bouncing, ContractStatus.renewing]
        
        let ref: Query<DocumentData> = query(collection(this.adminService.firestore, subscriptionsPath()), orderBy('createdAt', 'desc'), where('status', 'in', activeContractStatuses))
        
        // Apply enterprise filter if set
        if (enterpriseKey) {
            ref = query(ref, where('denormEnterpriseKey', '==', enterpriseKey))
        }
        
        collectionData(ref, { idField: 'documentId' }).subscribe(async (documents: any[]) => {
            this.subscriptions = []
            documents.forEach((documentData) => {
                let subscription = new Subscription(documentData, documentData.documentId)
                this.subscriptions.push(subscription)
            })
            this.resetInvoiceQueryPagination()
            await this.loadInvoices()
        })
    }

    private resetInvoiceQueryPagination() {
        this.pageIndex = 0
        this.cursorsAfterPage = []
        this.hasNextPage = false
    }

    async loadInvoices() {
        const startAfterDoc = this.pageIndex === 0 ? null : this.cursorsAfterPage[this.pageIndex - 1]
        const { docs, hasMore } = await this.invoiceService.listPage(this.status, this.itemsPerPage, startAfterDoc)
        this.hasNextPage = hasMore
        if (docs.length > 0) {
            this.cursorsAfterPage[this.pageIndex] = docs[docs.length - 1]
            this.invoices = docs.map((doc) => {
                const invoice = new Invoice(doc.data())
                invoice.$key = doc.id
                invoice.$subscription = this.subscriptions.find((sub) => sub.$key === invoice.subscriptionRef?.id)
                return invoice
            })
        } else {
            this.invoices = []
        }
    }

    getRangeStart(): number {
        return this.invoices.length === 0 ? 0 : this.pageIndex * this.itemsPerPage + 1
    }

    getRangeEnd(): number {
        return this.pageIndex * this.itemsPerPage + this.invoices.length
    }

    /** Total invoices for the current status filter (same query as the table). */
    getTotalForCurrentStatus(): number | undefined {
        return this.invoiceCounts[this.status]
    }

    /** Approximate page count from total ÷ page size (matches cursor pages). */
    getTotalPages(): number {
        const total = this.invoiceCounts[this.status]
        if (total == null || total === 0) {
            return 1
        }
        return Math.max(1, Math.ceil(total / this.itemsPerPage))
    }

    goToNextPage() {
        if (this.hasNextPage) {
            this.pageIndex++
            this.loadInvoices()
        }
    }

    goToPreviousPage() {
        if (this.pageIndex > 0) {
            this.pageIndex--
            this.loadInvoices()
        }
    }

    goToFirstPage() {
        if (this.pageIndex !== 0) {
            this.pageIndex = 0
            this.loadInvoices()
        }
    }

    onItemsPerPageChange() {
        this.resetInvoiceQueryPagination()
        this.loadInvoices()
    }

    changeStatus(event: any) {
        this.status = event.target.value as InvoiceStatus
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { status: this.status },
            queryParamsHandling: 'merge',
        })
        this.filterInvoices()
    }

    setStatus(status: InvoiceStatus) {
        this.status = status
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { status: this.status },
            queryParamsHandling: 'merge',
        })
        this.filterInvoices()
    }

    filterInvoices() {
        this.resetInvoiceQueryPagination()
        this.loadInvoices()
    }

    // Stats computed properties
    getDueCount(): number {
        return this.invoiceCounts[InvoiceStatus.DUE] || 0
    }

    getPaidCount(): number {
        return this.invoiceCounts[InvoiceStatus.PAID] || 0
    }

    getCancelledCount(): number {
        return this.invoiceCounts[InvoiceStatus.CANCELLED] || 0
    }

    getTotalDueValue(): number {
        return this.totalDueValue
    }

    async loadAllInvoiceCounts() {
        for (const status of this.invoiceStates) {
            this.invoiceCounts[status] = await this.invoiceService.countAllForStatus(status)
        }
        this.totalDueValue = await this.invoiceService.sumTotalDueValue()
    }

    viewInvoiceDetail(invoice: Invoice) {
        this.modalInvoiceTab = 'overview'
        this.modalInvoice = invoice
        this.showInvoiceDetailModal = true
        this.modalChangeLogs = []
        this.modalChangeLogsLoading = true
        this.modalChangeLogsError = null

        if (invoice.$key) {
            const invoiceRef = this.invoiceService.getDocRef(invoice.$key) as any
            this.adminChangeLogService
                .list(ChangeLogsEntityEnum.INVOICE, { invoiceRef })
                .then((logs) => {
                    this.modalChangeLogs = logs
                    this.modalChangeLogsLoading = false
                })
                .catch((error) => {
                    console.error('Failed to load invoice history', { invoiceKey: invoice.$key, error })
                    this.modalChangeLogsError = 'Unable to load invoice history right now.'
                    this.modalChangeLogsLoading = false
                })
        } else {
            this.modalChangeLogsLoading = false
        }
    }

    closeInvoiceDetailModal() {
        this.showInvoiceDetailModal = false
        this.modalInvoice = null
        this.modalChangeLogs = []
        this.modalChangeLogsError = null
        this.modalInvoiceTab = 'overview'
    }

    private async getAdminClientRefs() {
        const user = await this.authService.waitForUid()
        const admin = await this.clientService.getClientByUid(user.uid)
        if (!admin?.$key) {
            return null
        }

        return {
            admin,
            adminRef: this.clientService.getDocRef(admin.$key),
        }
    }

    private async addInvoiceChangeLog(invoice: Invoice, comment: string): Promise<boolean> {
        if (!invoice.$key) {
            return false
        }

        const adminContext = await this.getAdminClientRefs()
        if (!adminContext) {
            console.warn('Skipping invoice change log because no admin client record was found.')
            return false
        }

        await this.adminChangeLogService.addInvoiceChangeLog({
            invoiceRef: this.invoiceService.getDocRef(invoice.$key) as any,
            clientRef: invoice.clientRef as any,
            merchantRef: invoice.merchantRef as any,
            adminRef: adminContext.adminRef as any,
            comment: `${comment} by ${adminContext.admin.firstName}`,
        })

        return true
    }

    async showContextMenu(event: MouseEvent, invoice: Invoice) {
        this.globalService.contextMenuItems = []
        this.selectedInvoice = invoice
        this.globalService.showContextMenu(event)

        let markAsMenuItems: any = []
        if (invoice.status != InvoiceStatus.PAID) {
            markAsMenuItems.push({
                label: 'Mark as Paid',
                action: () => {
                    this.updateInvoiceStatus(invoice, InvoiceStatus.PAID, 'Marked as paid')
                },
            })
        }

        if (invoice.status != InvoiceStatus.DUE) {
            markAsMenuItems.push({
                label: 'Mark as Due',
                action: () => {
                    this.updateInvoiceStatus(invoice, InvoiceStatus.DUE, 'Marked as due')
                },
            })
        }

        if (invoice.status != InvoiceStatus.REFUNDED) {
            markAsMenuItems.push({
                label: 'Mark as Refunded',
                action: () => {
                    this.updateInvoiceStatus(invoice, InvoiceStatus.REFUNDED, 'Marked as refunded')
                },
            })
        }

        if (invoice.status != InvoiceStatus.CANCELLED) {
            markAsMenuItems.push({
                label: 'Mark as Cancelled',
                action: () => {
                    this.updateInvoiceStatus(invoice, InvoiceStatus.CANCELLED, 'Marked as cancelled')
                },
            })
        }

        if (invoice.status != InvoiceStatus.BREECHED) {
            markAsMenuItems.push({
                label: 'Mark as Breeched',
                action: () => {
                    this.updateInvoiceStatus(invoice, InvoiceStatus.BREECHED, 'Marked as breeched')
                },
            })
        }

        this.globalService.contextMenuItems.push({
            icon: 'fa fa-exchange',
            label: 'Update Status : ',
            subMenuItems: markAsMenuItems,
        })

        let changeTypeMenuItems: any = []
        if (invoice.paymentMethodType != SubscriptionType.PAYFAST) {
            changeTypeMenuItems.push({
                label: 'Change to Payfast',
                action: () => {
                    this.updateInvoiceType(invoice, SubscriptionType.PAYFAST)
                },
            })
        }

        if (invoice.paymentMethodType != SubscriptionType.PAYGATE) {
            changeTypeMenuItems.push({
                label: 'Change to PayGate',
                action: () => {
                    this.updateInvoiceType(invoice, SubscriptionType.PAYGATE)
                },
            })
        }

        if (invoice.paymentMethodType != SubscriptionType.EFT) {
            changeTypeMenuItems.push({
                label: 'Change to EFT',
                action: () => {
                    this.updateInvoiceType(invoice, SubscriptionType.EFT)
                },
            })
        }

        this.globalService.contextMenuItems.push({
            icon: 'fa fa-exchange',
            label: 'Change Payment Method: ',
            subMenuItems: changeTypeMenuItems,
        })
    }

    async updateInvoiceType(invoice: Invoice, type: SubscriptionType) {
        invoice.paymentMethodType = type
        await this.addInvoiceChangeLog(invoice, `Changed payment method to ${EnumNames.getSubscriptionType(type)}`)
        await this.invoiceService.update(invoice)
        this.globalService.hideContextMenu()
        // location.reload()
    }

    async updateInvoiceStatus(invoice: Invoice, status: InvoiceStatus, comment: string) {
        const logged = await this.addInvoiceChangeLog(invoice, comment)
        if (logged) {
            if (status === InvoiceStatus.PAID) {
                await this.invoiceService.markAsPaid(invoice)
            } else if (status === InvoiceStatus.BREECHED) {
                await this.invoiceService.markAsClientBreeched(invoice)
            } else if (status === InvoiceStatus.REFUNDED) {
                await this.invoiceService.markAsRefunded(invoice)
            } else if (status === InvoiceStatus.CANCELLED) {
                await this.invoiceService.markAsCancelled(invoice)
            } else if (status === InvoiceStatus.DUE) {
                await this.invoiceService.markAsDue(invoice)
            }
        }
        this.globalService.hideContextMenu()
        location.reload()
    }
}
